import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import { useActiveTeamStore } from './store/activeTeamStore';
import { useTeamId } from './hooks/useTeamId';
import { useTeamRole } from './hooks/useTeamRole';
import type { TeamRole } from './lib/roles';
import { authApi, teamApi } from './lib/api';
import { trackPageView, initAnalytics, setAnalyticsEnabled, hasAnalyticsConsent } from './lib/analytics';
import { isPublicRoute } from './lib/publicRoutes';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // App shell uses an inner scroll container; fall back to window for public pages.
    const inner = document.getElementById('app-scroll-container');
    if (inner) inner.scrollTo(0, 0);
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import TeamsPage from './pages/TeamsPage';
import TeamDetailPage from './pages/TeamDetailPage';
import SettingsPage from './pages/SettingsPage';
import HowToPage from './pages/HowToPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import PricingPage from './pages/NewPricingPage';
import DraftPricingPage from './pages/DraftPricingPage';
import FreeRedliningReviewPage from './pages/FreeRedliningReviewPage';
import DemoRequestPage from './pages/DemoRequestPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SupportPage from './pages/SupportPage';
import McpDocsPage from './pages/McpDocsPage';
import SecurityPage from './pages/SecurityPage';
import FeaturesPage from './pages/FeaturesPage';
import ContactPage from './pages/ContactPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import PendingReviewsPage from './pages/PendingReviewsPage';
import ComplianceHubPage from './pages/ComplianceHubPage';
import ComprehensiveExamPackagePage from './pages/ComprehensiveExamPackagePage';
import AnalyticsPage from './pages/AnalyticsPage';
import WebsiteScanPage from './pages/WebsiteScanPage';
import WebsiteScanDetailPage from './pages/WebsiteScanDetailPage';
import SocialMediaPage from './pages/SocialMediaPage';
import IntegrationsPage from './pages/IntegrationsPage';
import McpChecksPage from './pages/McpChecksPage';
import NarmiPartnerPage from './pages/NarmiPartnerPage';
import MBAPartnerPage from './pages/MBAPartnerPage';
import CrowePartnerPage from './pages/CrowePartnerPage';
import BatesPartnerPage from './pages/BatesPartnerPage';
import BraviantPartnerPage from './pages/BraviantPartnerPage';
import VaroPartnerPage from './pages/VaroPartnerPage';
import ArcsalusPartnerPage from './pages/ArcsalusPartnerPage';
import KleinPartnerPage from './pages/KleinPartnerPage';
import CustomRulesPage from './pages/CustomRulesPage';
import CradPage from './pages/CradPage';
import CradLargeBankListPage from './pages/CradLargeBankListPage';
import CradLargeBankPage from './pages/CradLargeBankPage';
import CraSbLoansPage from './pages/CraSbLoansPage';
import OrganizationDashboardPage from './pages/OrganizationDashboardPage';
import FirmBrandingPage from './pages/FirmBrandingPage';
import RaymondJamesPartnerPage from './pages/RaymondJamesPartnerPage';
import DDFPartnerPage from './pages/DDFPartnerPage';
import GNAPartnerPage from './pages/GNAPartnerPage';
import SmartBankPartnerPage from './pages/SmartBankPartnerPage';
import StephensPartnerPage from './pages/StephensPartnerPage';
import IncredibleBankPartnerPage from './pages/IncredibleBankPartnerPage';
import AlvarezMarsalPartnerPage from './pages/AlvarezMarsalPartnerPage';
import DuaneMorrisPartnerPage from './pages/DuaneMorrisPartnerPage';
import ABSPartnerPage from './pages/ABSPartnerPage';
import BBOKPartnerPage from './pages/BBOKPartnerPage';
import FNBBPartnerPage from './pages/FNBBPartnerPage';
import DentDemoPage from './pages/demo/DentDemoPage';
import NbeDemoPage from './pages/demo/NbeDemoPage';
import ClearbidDemoPage from './pages/demo/ClearbidDemoPage';
import MigratePage from './pages/MigratePage';
import ComplaintBulkImportPage from './pages/ComplaintBulkImportPage';
import ExamHistoryImportPage from './pages/ExamHistoryImportPage';
import PolicyBulkImportPage from './pages/PolicyBulkImportPage';
import FusionBankPage from './pages/FusionBankPage';
import NotFoundPage from './pages/NotFoundPage';
import HmdaUploadPage from './pages/HmdaUploadPage';
import AlertUploadPage from './pages/AlertUploadPage';
import AlertFileReportPage from './pages/AlertFileReportPage';
import LarBuilderPage from './pages/LarBuilderPage';
import LarWorkspacePage from './pages/LarWorkspacePage';
import LarFieldGuidePage from './pages/LarFieldGuidePage';
import HmdaValidationsPage from './pages/HmdaValidationsPage';
import HmdaReportPage from './pages/HmdaReportPage';
import FairLendingDashboardPage from './pages/FairLendingDashboardPage';
import FairLendingAnalysisPage from './pages/FairLendingAnalysisPage';
import FairLendingContentFindingsPage from './pages/FairLendingContentFindingsPage';
import RegressionAnalysisPage from './pages/RegressionAnalysisPage';
import FldDashboardPage from './pages/FldDashboardPage';
import BranchesPage from './pages/BranchesPage';
import CradHmdaLendingTestPage from './pages/CradHmdaLendingTestPage';
import FairLendingRedliningMapPage from './pages/FairLendingRedliningMapPage';
import MappingPage from './pages/MappingPage';
import OAuthConsentPage from './pages/OAuthConsentPage';
import AuditLogsPage from './pages/AuditLogsPage';
import BoardReportsPage from './pages/BoardReportsPage';
import BoardReportDetailPage from './pages/BoardReportDetailPage';
import ExamManagementPage from './pages/ExamManagementPage';
import ExamDetailPage from './pages/ExamDetailPage';
import ComplianceCalendarPage from './pages/ComplianceCalendarPage';
import ContentLibraryPage from './pages/ContentLibraryPage';
import ExceptionTrackingPage from './pages/ExceptionTrackingPage';
import HmdaQualityDashboardPage from './pages/HmdaQualityDashboardPage';
import PeerBenchmarkingPage from './pages/PeerBenchmarkingPage';
// Sprint 3
import EditIntelligencePage from './pages/EditIntelligencePage';
import GeocodingValidationPage from './pages/GeocodingValidationPage';
import AporRateSpreadPage from './pages/AporRateSpreadPage';
import LoanOfficerMonitoringPage from './pages/LoanOfficerMonitoringPage';
import RiskAssessmentPage from './pages/RiskAssessmentPage';
import RedliningAnalysisPage from './pages/RedliningAnalysisPage';
import CfpbFilingPage from './pages/CfpbFilingPage';
// Sprint 5
import PolicyManagementPage from './pages/PolicyManagementPage';
import PolicyDetailPage from './pages/PolicyDetailPage';
import ComplaintManagementPage from './pages/ComplaintManagementPage';
import ComplaintIntelligencePage from './pages/ComplaintIntelligencePage';
import ProceduresPage from './pages/ProceduresPage';
import ComplaintDetailPage from './pages/ComplaintDetailPage';
import VendorCompliancePage from './pages/VendorCompliancePage';
import TrainingManagementPage from './pages/TrainingManagementPage';
import TrainingDetailPage from './pages/TrainingDetailPage';
// Sprint 6
import RegulatoryKnowledgeBasePage from './pages/RegulatoryKnowledgeBasePage';
import ProductComplianceProfilesPage from './pages/ProductComplianceProfilesPage';
import CampaignManagementPage from './pages/CampaignManagementPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
// Sprint 4
import RegulatoryIntelligencePage from './pages/RegulatoryIntelligencePage';
import EnforcementIntelligencePage from './pages/EnforcementIntelligencePage';
import StateCompliancePage from './pages/StateCompliancePage';
import ExamReadinessPage from './pages/ExamReadinessPage';
import ClaridAgentPage from './pages/ClaridAgentPage';
// Sprint 0: AI Performance & Admin Health
import AiPerformancePage from './pages/AiPerformancePage';
import AdminSystemHealthPage from './pages/AdminSystemHealthPage';
// CRA Module
import CraDashboardPage from './pages/CraDashboardPage';
import BsaObligationsPage from './pages/BsaObligationsPage';
import BsaRiskAssessmentPage from './pages/BsaRiskAssessmentPage';
import BsaAssessmentListPage from './pages/BsaAssessmentListPage';
import BondsAnalysisPage from './pages/BondsAnalysisPage';
import BondsLibraryPage from './pages/BondsLibraryPage';
import CraAssessmentAreasPage from './pages/CraAssessmentAreasPage';
import CraLendingAnalysisPage from './pages/CraLendingAnalysisPage';
import CraCdActivitiesPage from './pages/CraCdActivitiesPage';
import CraGapAnalysisPage from './pages/CraGapAnalysisPage';
import CraPublicFilePage from './pages/CraPublicFilePage';
import Layout from './components/navigation/SidebarLayout';
import OnboardingWizard from './components/OnboardingWizard';
import InstitutionSetupWizard from './components/InstitutionSetupWizard';
import CookieConsent from './components/CookieConsent';
import ChatbotWidget from './components/ChatbotWidget';

/**
 * ProtectedRoute — gate a route by authentication and (optionally) team role.
 *
 * QA #4 #1 found that hiding sidebar links wasn't enough — a Viewer pasting
 * /billing into the URL bar still loaded the full Billing page. The fix is
 * to also gate the routes themselves, not just the nav. Routes that require
 * specific roles pass `requiredRoles={[...]}`.
 *
 * Behavior:
 *   - Not authenticated → redirect to /login
 *   - Authenticated but role still loading → render nothing (avoids flashing
 *     protected content for the half-second the role query is in flight)
 *   - Authenticated but role not in requiredRoles → redirect to /dashboard
 *   - Otherwise → render children
 */
function ProtectedRoute({
  children,
  requiredRoles,
}: {
  children: React.ReactNode;
  requiredRoles?: TeamRole[];
}) {
  const { isAuthenticated } = useAuthStore();
  const { teamId, isLoading: teamLoading } = useTeamId();
  const { role, isLoading: roleLoading } = useTeamRole(teamId);
  const location = useLocation();

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} />;
  }

  // If this route requires specific roles, wait for the role query to
  // resolve before rendering. Without this guard the protected page
  // briefly flashes for users who aren't authorized.
  if (requiredRoles) {
    if (teamLoading || roleLoading) return null;
    if (!role || !requiredRoles.includes(role)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    // Measure the public marketing site only. A compliance product must never send
    // authenticated-app activity to Google, and analytics stays off until the visitor
    // accepts the cookie banner. isPublicRoute + hasAnalyticsConsent enforce both.
    const allowed = hasAnalyticsConsent() && isPublicRoute(location.pathname);
    setAnalyticsEnabled(allowed);
    if (allowed) {
      initAnalytics();
      trackPageView(location.pathname);
    }
  }, [location.pathname]);
  return null;
}

function App() {
  const { isAuthenticated, setUser } = useAuthStore();
  const location = useLocation();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showInstitutionSetup, setShowInstitutionSetup] = useState(false);
  const [setupDismissed, setSetupDismissed] = useState(
    () => sessionStorage.getItem('institutionSetupDismissed') === 'true'
  );

  // Fetch current user on mount if authenticated
  const { data } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await authApi.getCurrentUser();
      return response.data;
    },
    enabled: isAuthenticated,
  });

  // Fetch teams to check institution setup status. Uses the same namespaced
  // queryKey as the useTeamId hook so they share the cache without colliding
  // with page-local ['teams'] queries that store an unwrapped shape.
  const { data: teamsData } = useQuery({
    queryKey: ['teams', 'list-axios-response'],
    queryFn: () => teamApi.getTeams(),
    enabled: isAuthenticated && !!data,
    staleTime: 5 * 60 * 1000,
  });
  // Resolve the user's ACTIVE team (the tenant they've switched to), not just
  // teams[0]. Mirrors useTeamId's precedence (active → owned → member → first)
  // but reuses this component's auth-gated query instead of calling the hook
  // here (which would fetch /teams ungated on public routes). Without this, the
  // institution-setup wizard evaluated ownership against the wrong tenant for a
  // consultant who belongs to multiple banks. See hooks/useTeamId.ts.
  const activeTeamId = useActiveTeamStore((s) => s.activeTeamId);
  const teamsPayload = teamsData?.data;
  const teamId = ((): string | undefined => {
    if (!teamsPayload) return undefined;
    const allIds = new Set((teamsPayload.teams ?? []).map((t: any) => t.id));
    if (activeTeamId && allIds.has(activeTeamId)) return activeTeamId;
    return (
      teamsPayload.ownedTeams?.[0]?.id ??
      teamsPayload.memberTeams?.[0]?.id ??
      teamsPayload.teams?.[0]?.id
    );
  })();
  const isTeamOwner = teamsPayload?.ownedTeams?.some((t: any) => t.id === teamId) ?? false;

  // Fetch institution setup status for the team
  const { data: setupStatus } = useQuery({
    queryKey: ['institutionSetup', teamId],
    queryFn: () => teamApi.getInstitutionSetup(teamId!).then(r => r.data),
    enabled: !!teamId && !showOnboarding,
  });

  useEffect(() => {
    if (data) {
      setUser(data);

      // Show onboarding wizard only for brand-new accounts that have never updated their profile.
      // createdAt === updatedAt means no PATCH /me has been made (institution type was never explicitly set).
      if (data.createdAt && data.updatedAt && data.createdAt === data.updatedAt) {
        setShowOnboarding(true);
      }
    }
  }, [data, setUser]);

  // Auto-trigger institution setup: only for team owner, only on protected routes, once per session
  useEffect(() => {
    if (
      !showOnboarding &&
      setupStatus && !setupStatus.setupCompleted &&
      teamId && isTeamOwner &&
      !setupDismissed &&
      !isPublicRoute(location.pathname)
    ) {
      setShowInstitutionSetup(true);
    }
  }, [showOnboarding, setupStatus, teamId, isTeamOwner, setupDismissed, location.pathname]);

  return (
    <>
      <ScrollToTop />
      <PageTracker />
      <OnboardingWizard
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />
      {teamId && (
        <InstitutionSetupWizard
          isOpen={showInstitutionSetup && !showOnboarding}
          onClose={() => {
            setShowInstitutionSetup(false);
            setSetupDismissed(true);
            sessionStorage.setItem('institutionSetupDismissed', 'true');
          }}
          teamId={teamId}
        />
      )}
      <CookieConsent />
      <ChatbotWidget />
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/request-demo" element={<DemoRequestPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/pricing-draft" element={<DraftPricingPage />} />
      <Route path="/free-redlining-review" element={<FreeRedliningReviewPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/docs/mcp" element={<McpDocsPage />} />
      <Route path="/mcp-docs" element={<Navigate to="/docs/mcp" replace />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" />} />
      <Route path="/terms-of-service" element={<Navigate to="/terms" />} />
      <Route path="/invites/:token" element={<AcceptInvitePage />} />
      <Route path="/partners/narmi" element={<NarmiPartnerPage />} />
      <Route path="/partners/mba" element={<MBAPartnerPage />} />
      <Route path="/partners/crowe" element={<CrowePartnerPage />} />
      <Route path="/partners/bates" element={<BatesPartnerPage />} />
      <Route path="/partners/braviant" element={<BraviantPartnerPage />} />
      <Route path="/partners/varo" element={<VaroPartnerPage />} />
      <Route path="/partners/arcsalus" element={<ArcsalusPartnerPage />} />
      <Route path="/partners/cathy-klein" element={<KleinPartnerPage />} />
      {/* Legacy /partners/sentry — Arcsalus rebranded from Sentry Advisors on
          2026-04-28. Keep this redirect so the original link Bobby shared
          before the rebrand still resolves. */}
      <Route path="/partners/sentry" element={<ArcsalusPartnerPage />} />
      <Route path="/partners/raymond-james" element={<RaymondJamesPartnerPage />} />
      <Route path="/partners/ddf" element={<DDFPartnerPage />} />
      <Route path="/partners/gna" element={<GNAPartnerPage />} />
      <Route path="/partners/smartbank" element={<SmartBankPartnerPage />} />
      <Route path="/partners/stephens" element={<StephensPartnerPage />} />
      <Route path="/partners/incrediblebank" element={<IncredibleBankPartnerPage />} />
      <Route path="/partners/alvarez-marsal" element={<AlvarezMarsalPartnerPage />} />
      <Route path="/partners/duane-morris" element={<DuaneMorrisPartnerPage />} />
      <Route path="/partners/abs" element={<ABSPartnerPage />} />
      <Route path="/partners/bbok" element={<BBOKPartnerPage />} />
      <Route path="/partners/fnbb" element={<FNBBPartnerPage />} />
      <Route path="/demo/dent" element={<DentDemoPage />} />
      <Route path="/demo/kielich" element={<NbeDemoPage />} />
      <Route path="/demo/novembre" element={<ClearbidDemoPage />} />
      <Route path="/migrate" element={<MigratePage />} />
      <Route path="/partners/fusion-bank" element={<FusionBankPage />} />
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />

      {/* Protected routes */}
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <Layout>
              <OrganizationDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Partner client report branding (firm admins). No nav item in v1 — linked from the Client report modal. */}
      <Route
        path="/firm-branding"
        element={
          <ProtectedRoute>
            <Layout>
              <FirmBrandingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute
            requiredRoles={['OWNER', 'COMPLIANCE_OFFICER', 'MARKETING_MANAGER', 'MARKETING_MEMBER']}
          >
            <Layout>
              <UploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <ReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ReportDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-reviews"
        element={
          <ProtectedRoute>
            <Layout>
              <PendingReviewsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <TeamsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:id"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <TeamDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/how-to"
        element={
          <ProtectedRoute>
            <Layout>
              <HowToPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <BillingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance-hub"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplianceHubPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packages"
        element={
          <ProtectedRoute>
            <Layout>
              <ComprehensiveExamPackagePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Layout>
              <AnalyticsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <AdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/website-scan"
        element={
          <ProtectedRoute>
            <Layout>
              <WebsiteScanPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/website-scan/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <WebsiteScanDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/social-media"
        element={
          <ProtectedRoute>
            <Layout>
              <SocialMediaPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <IntegrationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mcp-checks"
        element={
          <ProtectedRoute>
            <Layout>
              <McpChecksPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* LAR Builder routes */}
      <Route
        path="/hmda/lar-builder"
        element={
          <ProtectedRoute>
            <Layout>
              <LarBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hmda/lar-builder/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <LarWorkspacePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hmda/field-guide"
        element={
          <ProtectedRoute>
            <Layout>
              <LarFieldGuidePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* HMDA routes */}
      <Route
        path="/hmda/lar-validation"
        element={
          <ProtectedRoute>
            <Layout>
              <HmdaUploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* /hmda is no longer a leaf — it's the module namespace. Redirect bare
          /hmda visits to the LAR Validation entry point. */}
      <Route path="/hmda" element={<Navigate to="/hmda/lar-validation" replace />} />
      <Route
        path="/hmda/validations"
        element={
          <ProtectedRoute>
            <Layout>
              <HmdaValidationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hmda/validations/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <HmdaReportPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ALERT / ILDR routes */}
      <Route
        path="/alert/upload"
        element={
          <ProtectedRoute>
            <Layout>
              <AlertUploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/alert" element={<Navigate to="/alert/upload" replace />} />
      <Route
        path="/alert/files/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <AlertFileReportPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Audit Logs */}
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute requiredRoles={['OWNER', 'COMPLIANCE_OFFICER']}>
            <Layout>
              <AuditLogsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Board Reports */}
      <Route
        path="/board-reports"
        element={
          <ProtectedRoute>
            <Layout>
              <BoardReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/board-reports/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <BoardReportDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Content Library */}
      <Route
        path="/content-library"
        element={
          <ProtectedRoute>
            <Layout>
              <ContentLibraryPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Exam Management */}
      <Route
        path="/exams"
        element={
          <ProtectedRoute>
            <Layout>
              <ExamManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exams/bulk-import"
        element={
          <ProtectedRoute>
            <Layout>
              <ExamHistoryImportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exams/:examId"
        element={
          <ProtectedRoute>
            <Layout>
              <ExamDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Compliance Calendar */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplianceCalendarPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Exception Tracking */}
      <Route
        path="/exceptions"
        element={
          <ProtectedRoute>
            <Layout>
              <ExceptionTrackingPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* HMDA Quality Monitoring */}
      <Route
        path="/hmda/quality"
        element={
          <ProtectedRoute>
            <Layout>
              <HmdaQualityDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Peer Benchmarking */}
      <Route
        path="/peer-benchmarking"
        element={
          <ProtectedRoute>
            <Layout>
              <PeerBenchmarkingPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fair Lending routes */}
      <Route
        path="/fair-lending"
        element={
          <ProtectedRoute>
            <Layout>
              <FairLendingDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fair-lending/analyses/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <FairLendingAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fair-lending/content-findings"
        element={
          <ProtectedRoute>
            <Layout>
              <FairLendingContentFindingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fair-lending/regression"
        element={
          <ProtectedRoute>
            <Layout>
              <RegressionAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/fair-lending/fld/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <FldDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/branches"
        element={
          <ProtectedRoute>
            <Layout>
              <BranchesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/cra/lending-test"
        element={
          <ProtectedRoute>
            <Layout>
              <CradHmdaLendingTestPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/fair-lending/redlining-map"
        element={
          <ProtectedRoute>
            <Layout>
              <FairLendingRedliningMapPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/mapping"
        element={
          <ProtectedRoute>
            <Layout>
              <MappingPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Sprint 3: HMDA & Fair Lending Depth */}
      <Route
        path="/hmda/edit-intelligence"
        element={
          <ProtectedRoute>
            <Layout>
              <EditIntelligencePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hmda/geocoding"
        element={
          <ProtectedRoute>
            <Layout>
              <GeocodingValidationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hmda/apor"
        element={
          <ProtectedRoute>
            <Layout>
              <AporRateSpreadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fair-lending/loan-officers"
        element={
          <ProtectedRoute>
            <Layout>
              <LoanOfficerMonitoringPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Legacy redirect: old path before LO Monitoring was moved into Fair Lending */}
      <Route
        path="/hmda/loan-officers"
        element={<Navigate to="/fair-lending/loan-officers" replace />}
      />
      <Route
        path="/fair-lending/risk-assessments"
        element={
          <ProtectedRoute>
            <Layout>
              <RiskAssessmentPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fair-lending/redlining"
        element={
          <ProtectedRoute>
            <Layout>
              <RedliningAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hmda/cfpb-filing"
        element={
          <ProtectedRoute>
            <Layout>
              <CfpbFilingPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* CRA Module */}
      <Route
        path="/cra"
        element={
          <ProtectedRoute>
            <Layout>
              <CraDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* BSA/AML risk assessments — list + create */}
      <Route
        path="/bsa/assessments"
        element={
          <ProtectedRoute>
            <Layout>
              <BsaAssessmentListPage />
            </Layout>
          </ProtectedRoute>
        }
      />


      {/* BSA/AML risk assessment (CLARID-SPEC-BSA-001 §3.3) */}
      <Route
        path="/bsa/assessments/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <BsaRiskAssessmentPage />
            </Layout>
          </ProtectedRoute>
        }
      />



      {/* BSA/AML obligation register (CLARID-SPEC-BSA-001 — feature-gated server-side) */}
      <Route
        path="/bsa/obligations"
        element={
          <ProtectedRoute>
            <Layout>
              <BsaObligationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Bond Analysis (RJ partnership — feature-gated server-side) */}
      <Route
        path="/bonds"
        element={
          <ProtectedRoute>
            <Layout>
              <BondsAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bonds/library"
        element={
          <ProtectedRoute>
            <Layout>
              <BondsLibraryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/assessment-areas"
        element={
          <ProtectedRoute>
            <Layout>
              <CraAssessmentAreasPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/lending-analysis"
        element={
          <ProtectedRoute>
            <Layout>
              <CraLendingAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/cd-activities"
        element={
          <ProtectedRoute>
            <Layout>
              <CraCdActivitiesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/gap-analysis"
        element={
          <ProtectedRoute>
            <Layout>
              <CraGapAnalysisPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/public-file"
        element={
          <ProtectedRoute>
            <Layout>
              <CraPublicFilePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/cra/sb-loans"
        element={
          <ProtectedRoute>
            <Layout>
              <CraSbLoansPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/crad/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CradPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/large-bank"
        element={
          <ProtectedRoute>
            <Layout>
              <CradLargeBankListPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cra/large-bank/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CradLargeBankPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Clarid Agent: Ask Clarid (CLARID-SPEC-AGENT-001) */}
      <Route
        path="/agent"
        element={
          <ProtectedRoute>
            <Layout>
              <ClaridAgentPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Exam Readiness */}
      <Route
        path="/exam-readiness"
        element={
          <ProtectedRoute>
            <Layout>
              <ExamReadinessPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Sprint 4: Regulatory Intelligence */}
      <Route
        path="/regulatory-intelligence"
        element={
          <ProtectedRoute>
            <Layout>
              <RegulatoryIntelligencePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/enforcement-intelligence"
        element={
          <ProtectedRoute>
            <Layout>
              <EnforcementIntelligencePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/state-compliance"
        element={
          <ProtectedRoute>
            <Layout>
              <StateCompliancePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Sprint 5: Policy, Complaint, Vendor Compliance */}
      <Route
        path="/policies"
        element={
          <ProtectedRoute>
            <Layout>
              <PolicyManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies/bulk-import"
        element={
          <ProtectedRoute>
            <Layout>
              <PolicyBulkImportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/policies/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <PolicyDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplaintManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints/bulk-import"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplaintBulkImportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints/intelligence"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplaintIntelligencePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procedures"
        element={
          <ProtectedRoute>
            <Layout>
              <ProceduresPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/complaints/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ComplaintDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-compliance"
        element={
          <ProtectedRoute>
            <Layout>
              <VendorCompliancePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <Layout>
              <TrainingManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <TrainingDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Sprint 6: Platform Infrastructure */}
      <Route
        path="/regulations"
        element={
          <ProtectedRoute>
            <Layout>
              <RegulatoryKnowledgeBasePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-profiles"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductComplianceProfilesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-rules"
        element={
          <ProtectedRoute>
            <Layout>
              <CustomRulesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Layout>
              <CampaignManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CampaignDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Sprint 0: AI Performance & Admin Health */}
      <Route
        path="/ai-performance"
        element={
          <ProtectedRoute>
            <Layout>
              <AiPerformancePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system-health"
        element={
          <ProtectedRoute requiredRoles={['OWNER']}>
            <Layout>
              <AdminSystemHealthPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Legacy flat-route redirects → nested canonical paths.
          Preserves bookmarks, external links, and integrations from before
          the Wave 4 nested rewrite. Safe to remove after a few months. */}
      <Route path="/cfpb-filing" element={<Navigate to="/hmda/cfpb-filing" replace />} />
      <Route path="/redlining-analysis" element={<Navigate to="/fair-lending/redlining" replace />} />
      <Route path="/risk-assessments" element={<Navigate to="/fair-lending/risk-assessments" replace />} />

      {/* Catch all — show a real 404 instead of silently bouncing to marketing */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  );
}

export default App;
